import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  doc,
  getDoc,
  addDoc,
  collection,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { getAuth } from "firebase/auth";
import { saveResponse } from "../db"; // Spara svaren till din databas
import { getTenantIdFromHost } from "../Tenant/getTenantId";
import SurveyQuestions from "./SurveyQuestions";
import InfoModal from "./InfoModal";

export default function PublicFeedback() {
  const auth = getAuth();
  const { token } = useParams();
  const tenantId = getTenantIdFromHost();

  const [invite, setInvite] = useState(null);
  const [headerLogo, setHeaderLogo] = useState("/kundresa_logo.png");
  const [surveyId, setSurveyId] = useState(null);
  const [questions, setQuestions] = useState(null);
  const [state, setState] = useState("loading"); // loading | open | submitted | invalid | error | done
  const [isSaving, setIsSaving] = useState(false);

  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [preferPhone, setPreferPhone] = useState(false);

  const [responses, setResponses] = useState([]);
  const [comments, setComments] = useState({});
  const [errors, setErrors] = useState({});

  const [showCloseModal, setShowCloseModal] = useState(false);
  const [warningText, setWarningText] = useState("");

  const isSignedIn = !!auth.currentUser;
  useEffect(() => {
    if (!invite) {
      return;
    }
    const metaTitle = document.querySelector("meta[property='og:title']");
    const metaDesc = document.querySelector("meta[property='og:description']");
    const metaImage = document.querySelector("meta[property='og:image']");

    if (tenantId === "arcus") {
      metaTitle.setAttribute(
        "content",
        invite.officeId + "– Lämna din feedback",
      );
      metaDesc.setAttribute("content", "Det tar bara 2–3 minuter.");
      metaImage.setAttribute(
        "content",
        "https://arcus.kundupplevelse.com/logos/preview.jpg",
      );
    }

    if (invite.officeId.trim().toLowerCase().includes("slottsstaden")) {
      setHeaderLogo("/logos/slottsstaden_logo.png");
    } else if (tenantId === "arcus") {
      setHeaderLogo("/logos/arcus_logo.png");
    } else {
      setHeaderLogo("/kundresa_logo.png");
    }
  }, [invite]);

  useEffect(() => {
    (async () => {
      try {
        if (!tenantId || !token) return setState("invalid");

        const inviteSnap = await getDoc(
          doc(db, "tenants", tenantId, "surveyInvites", token),
        );
        if (!inviteSnap.exists()) return setState("invalid");

        const inv = inviteSnap.data();

        if (inv.status === "submitted") {
          setState("submitted");
          return;
        }

        if (inv.status === "callback_requested" && !isSignedIn) {
          setState("callback_requested");
          return;
        }

        if (
          inv.status !== "open" &&
          !(inv.status === "callback_requested" && isSignedIn)
        ) {
          setState("invalid");
          return;
        }

        setInvite(inv);
        setContactEmail(inv.email || "");
        setPreferPhone(!!inv.preferPhone);
        setContactPhone(inv.contactPhone || "");
        setQuestions(inv.questions);
        setSurveyId(inv.surveyId);
        setResponses(new Array((inv.questions || []).length).fill(""));
        setComments({});
        setState("open");
      } catch (e) {
        console.error(e);
        setState("error");
      }
    })();
  }, [tenantId, token]);

  useEffect(() => {
    if (isSignedIn) return;
    const markAsOpened = async () => {
      try {
        if (!tenantId || !token || !invite || state !== "open") return;

        const inviteRef = doc(db, "tenants", tenantId, "surveyInvites", token);

        const updateData = {
          opened: true,
          lastOpenedAt: serverTimestamp(),
        };

        if (!invite.firstOpenedAt) {
          updateData.firstOpenedAt = serverTimestamp();
        }

        await updateDoc(inviteRef, updateData);
      } catch (err) {
        console.error("Kunde inte markera enkäten som öppnad:", err);
      }
    };

    markAsOpened();
  }, [tenantId, token, invite, state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving) return;

    // const allAnswered = responses.every(
    //   (r) => r !== null && r !== undefined && r !== "",
    // );

    const allAnswered = responses.every((r, i) => {
      const q = questions[i];

      if (q.type === "text") return true;

      if (q.type === "checkbox") {
        if (Array.isArray(r)) return r.length > 0;
        if (typeof r === "string") return r.trim() !== "";
        return false;
      }

      return r !== null && r !== undefined && r !== "";
    });

    if (preferPhone) {
      if (!contactPhone) {
        setShowCloseModal(true);
        setWarningText("Fyll i telefonnummer så vi kan ringa upp.");

        return;
      }

      if (contactPhone.length < 8 || contactPhone.length > 20) {
        setShowCloseModal(true);
        setWarningText(
          "Telefonnumret verkar inte vara korrekt. Ange t.ex. 0701234567.",
        );

        return;
      }
    }

    if (!preferPhone && !allAnswered) {
      setShowCloseModal(true);
      setWarningText("Du behöver besvara alla frågor innan du kan skicka in.");

      return;
    }

    try {
      setIsSaving(true);

      const inviteRef = doc(db, "tenants", tenantId, "surveyInvites", token);

      if (preferPhone && !isSignedIn) {
        // Ingen response skapas – bara markera invite för uppföljning
        await updateDoc(inviteRef, {
          status: "callback_requested",
          submittedAt: serverTimestamp(),
          surveyId: surveyId,
          email: contactEmail.trim(),
          contactPhone: contactPhone.trim(),
          preferPhone: true,
        });
      } else {
        // Vanligt fullständigt svar

        const responseData = {
          surveyId: surveyId,
          responses,
          comments,
          inviteToken: token,
          customerName: invite.customerName || "",
          contactEmail: contactEmail.trim(),
          contactPhone: contactPhone.trim(),
          preferPhone: false,
          customerId: invite.customerId || null,
          officeId: invite.officeId || null,
          employeeId: invite.employeeId || null,
          createdBy: isSignedIn
            ? auth.currentUser.email + " ( - tel. på begäran)"
            : "public",
          status: "submitted",
        };

        const responseRefId = await saveResponse(responseData);

        await updateDoc(inviteRef, {
          status: "submitted",
          submittedAt: serverTimestamp(),
          responseId: responseRefId,
          surveyId: surveyId,
          email: contactEmail.trim(),
          contactPhone: contactPhone.trim(),
          preferPhone: false,
        });
      }
      if (preferPhone) {
        setState("callback_requested");
      } else {
        setState("done");
      }
    } catch (err) {
      console.error(err);
      setShowCloseModal(true);
      setWarningText("Kunde inte skicka in. Försök igen senare.");
    } finally {
      setIsSaving(false);
    }
  };

  const getInfoCard = (text, thanks) => {
    return (
      <>
        <div className="publicHeader">
          {tenantId ? (
            // <img
            //   className="indexLogo"
            //   src={`/logos/${tenantId}_logo.png`}
            //   alt={`${tenantId} logo`}
            // />
            <img
              className="indexLogo"
              src={headerLogo}
              alt={`${tenantId} logo`}
            />
          ) : (
            <img
              className="indexLogo"
              src={"/kundresa_logo.png"}
              alt="Kundupplevelse logo"
            />
          )}
        </div>
        <div
          className="visualizeWrapper transpWrapp"
          style={{ marginTop: 50, textAlign: "center" }}
        >
          {thanks ? (
            <>
              <h3 style={{color: "#444"}}><b>{thanks}</b> </h3><br />{" "}
            </>
          ) : null}
          <p>{text}</p>
          <div className="devider50"></div>
          {/* <p>
            <a className="smallLink" href="/privacy">
              Integritetspolicy
            </a>
          </p> */}
        </div>
      </>
    );
  };

  if (state === "loading") return <p>Laddar…</p>;
  if (state === "invalid") return getInfoCard("Länken är inte giltig.");
  if (state === "submitted")
    return getInfoCard("Enkäten är redan inskickad.", "Tack för att du tog dig tid");
  if (state === "callback_requested")
    return getInfoCard("Vi kommer att kontakta dig via telefon.", "Tack!");
  if (state === "done")
    return getInfoCard("Din återkoppling är nu inskickad och kommer att tas om hand.", "Tack för att du tog dig tid");
  if (state === "error") return getInfoCard("Denna sida är inte längre aktiv.");

  return (
    <div className="publicPage">
      <div className="publicHeader">
        {tenantId ? (
          // <img
          //   className="indexLogo"
          //   src={`/logos/${tenantId}_logo.png`}
          //   alt={`${tenantId} logo`}
          // />
          <img
            className="indexLogo"
            src={headerLogo}
            alt={`${tenantId} logo`}
          />
        ) : (
          <img
            className="indexLogo"
            src={"/kundresa_logo.png"}
            alt="Kundupplevelse logo"
          />
        )}
      </div>
      <div className="officeTitle">{invite.officeId}</div>
      <div className="publicCard">
        <div className="publicBody">
          {invite?.status === "callback_requested" && isSignedIn ? (
            <>
              <h5>👋👋 {auth.currentUser?.email}</h5>
              <h4>
                Den här kunden har bett om telefonkontakt. Använd detta formulär
                för undersökningen.
              </h4>
            </>
          ) : (
            <h1 className="publicTitle">
              Tack för att du delar din återkoppling
            </h1>
          )}

          {invite?.customerName ? (
            <p>
              Hej <b>{invite.customerName},</b>
            </p>
          ) : null}

          <p className="muted">Det tar 2–3 minuter att svara på frågorna.</p>

          <form onSubmit={handleSubmit}>
            {/* <input
                placeholder="E-post (valfritt)"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
              /> */}
            <div className="qWrapper">
              <label className="checkboxRow">
                <input
                  type="checkbox"
                  checked={preferPhone}
                  onChange={(e) => setPreferPhone(e.target.checked)}
                />
                Jag vill hellre bli kontaktad via telefon
              </label>
            </div>
            {preferPhone ? (
              <>
                {" "}
                <div className="labelWrapper">
                  <label className="checkboxRow" htmlFor="phone">
                    Telefon
                  </label>
                </div>{" "}
                <input
                  id="phone"
                  type="tel"
                  placeholder="Telefon"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                />
              </>
            ) : (
              <SurveyQuestions
                questions={questions}
                responses={responses}
                setResponses={setResponses}
                comments={comments}
                setComments={setComments}
                errors={errors}
                setErrors={setErrors}
              />
            )}

            <button type="submit" disabled={isSaving}>
              {isSaving ? "Skickar…" : "Skicka in"}
            </button>
          </form>
        </div>
        {showCloseModal && (
          <InfoModal
            btn2="Stäng"
            onConfirm={() => {
              setWarningText(null);
              setShowCloseModal(false);
            }}
          >
            <p>{warningText}</p>
          </InfoModal>
        )}
      </div>
    </div>
  );
}
